import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, TrendingUp, Plane, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { getLocalToday } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function StatisticsPage() {
    const { airline } = useAuth();
    const [stats, setStats] = useState(null);
    const [flightStats, setFlightStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('30');

    useEffect(() => {
        fetchData();
    }, [period]);

    const fetchData = async () => {
        try {
            const today = getLocalToday();
            const [overviewRes, flightStatsRes] = await Promise.all([
                axios.get(`${API}/statistics/overview`, { params: { local_date: today } }),
                axios.get(`${API}/statistics/flights?days=${period}`)
            ]);
            setStats(overviewRes.data);
            setFlightStats(flightStatsRes.data);
        } catch (error) {
            toast.error('Error al cargar estadísticas');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="space-y-4 animate-fade-in">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="h-64 bg-muted rounded-lg skeleton-pulse" />
            ))}
        </div>;
    }

    // Prepare data for charts
    const statusData = stats?.flight_status_breakdown ? 
        Object.entries(stats.flight_status_breakdown).map(([status, count]) => ({
            name: status === 'scheduled' ? 'Programado' :
                  status === 'departed' ? 'Despegado' :
                  status === 'arrived' ? 'Llegado' :
                  status === 'delayed' ? 'Demorado' :
                  status === 'cancelled' ? 'Cancelado' : status,
            value: count
        })) : [];

    const dailyFlightData = flightStats.map(day => ({
        date: day._id,
        total: day.total,
        onTime: day.on_time,
        delayed: day.delayed,
        cancelled: day.cancelled
    }));

    const onTimePercentage = flightStats.length > 0 ? 
        Math.round((flightStats.reduce((sum, d) => sum + d.on_time, 0) / 
                   flightStats.reduce((sum, d) => sum + d.total, 0)) * 100) || 0 : 0;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="statistics-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                        Análisis y Estadísticas
                    </h1>
                    <p className="text-muted-foreground">
                        Métricas de operaciones de {airline?.name || 'la aerolínea'}
                    </p>
                </div>
                <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-48" data-testid="period-select">
                        <SelectValue placeholder="Período" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">Últimos 7 días</SelectItem>
                        <SelectItem value="30">Últimos 30 días</SelectItem>
                        <SelectItem value="90">Últimos 90 días</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-aviation" data-testid="kpi-total-flights">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Plane className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">{stats?.total_flights || 0}</p>
                                <p className="text-xs text-muted-foreground uppercase">Total Vuelos</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation" data-testid="kpi-on-time">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">{onTimePercentage}%</p>
                                <p className="text-xs text-muted-foreground uppercase">Puntualidad</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation" data-testid="kpi-delayed">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                                <Clock className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">
                                    {stats?.flight_status_breakdown?.delayed || 0}
                                </p>
                                <p className="text-xs text-muted-foreground uppercase">Demorados</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation" data-testid="kpi-cancelled">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                                <XCircle className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <p className="font-mono text-2xl font-bold">
                                    {stats?.flight_status_breakdown?.cancelled || 0}
                                </p>
                                <p className="text-xs text-muted-foreground uppercase">Cancelados</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Daily Flights Chart */}
                <Card className="card-aviation" data-testid="chart-daily-flights">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xl font-bold uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-primary" />
                            Vuelos por Día
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {dailyFlightData.length === 0 ? (
                            <div className="h-64 flex items-center justify-center text-muted-foreground">
                                No hay datos disponibles
                            </div>
                        ) : (
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dailyFlightData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                                            tickFormatter={(value) => value.slice(5)}
                                        />
                                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <Bar dataKey="total" fill="#0EA5E9" name="Total" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Status Distribution Chart */}
                <Card className="card-aviation" data-testid="chart-status-distribution">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xl font-bold uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Distribución por Estado
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {statusData.length === 0 ? (
                            <div className="h-64 flex items-center justify-center text-muted-foreground">
                                No hay datos disponibles
                            </div>
                        ) : (
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={statusData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {statusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'hsl(var(--card))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Performance Trend */}
            <Card className="card-aviation" data-testid="chart-performance-trend">
                <CardHeader className="pb-2">
                    <CardTitle className="text-xl font-bold uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-primary" />
                        Tendencia de Rendimiento
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {dailyFlightData.length === 0 ? (
                        <div className="h-64 flex items-center justify-center text-muted-foreground">
                            No hay datos disponibles
                        </div>
                    ) : (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={dailyFlightData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                                        tickFormatter={(value) => value.slice(5)}
                                    />
                                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'hsl(var(--card))',
                                            border: '1px solid hsl(var(--border))',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Legend />
                                    <Line 
                                        type="monotone" 
                                        dataKey="onTime" 
                                        stroke="#10B981" 
                                        strokeWidth={2}
                                        name="A Tiempo"
                                        dot={{ fill: '#10B981', r: 4 }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="delayed" 
                                        stroke="#F59E0B" 
                                        strokeWidth={2}
                                        name="Demorados"
                                        dot={{ fill: '#F59E0B', r: 4 }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="cancelled" 
                                        stroke="#EF4444" 
                                        strokeWidth={2}
                                        name="Cancelados"
                                        dot={{ fill: '#EF4444', r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Operations Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="card-aviation">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-bold uppercase font-['Barlow_Condensed']">
                            Flota
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Total Aeronaves</span>
                                <span className="font-mono font-bold">{stats?.total_aircraft || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Operativas</span>
                                <span className="font-mono font-bold text-emerald-500">{stats?.operational_aircraft || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Utilización</span>
                                <span className="font-mono font-bold">
                                    {stats?.total_aircraft ? Math.round((stats.operational_aircraft / stats.total_aircraft) * 100) : 0}%
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-bold uppercase font-['Barlow_Condensed']">
                            Tripulaciones
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Total Tripulantes</span>
                                <span className="font-mono font-bold">{stats?.total_crew || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Vuelos Hoy</span>
                                <span className="font-mono font-bold text-primary">{stats?.today_flights || 0}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-bold uppercase font-['Barlow_Condensed']">
                            Mantenimiento
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Pendientes</span>
                                <span className="font-mono font-bold text-amber-500">{stats?.pending_maintenance || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Diferidos Activos</span>
                                <span className="font-mono font-bold text-red-500">{stats?.active_deferrals || 0}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
